/**
 * HYPSEYA — Smart Notifications
 * Two-layer system:
 * 1. Web Push (VAPID) — works when app is closed, via service worker
 * 2. Browser Notification API — fallback when SW not available (local only)
 *
 * On first load: requests permission → subscribes to push → registers with backend.
 * Subsequent loads: checks subscription is still active, re-registers if needed.
 */

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
const STORAGE_KEY = 'hyp_notif_last_scheduled';
const PERMISSION_ASKED_KEY = 'hyp_notif_permission_asked';
const WEB_PUSH_REGISTERED_KEY = 'hyp_web_push_registered';

// ── VAPID key conversion ──────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64   = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw      = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// ── Register service worker + subscribe to push ───────────────────────────────
async function subscribeToPush(userId) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (!VAPID_PUBLIC_KEY) return false;

  try {
    // Register SW if not already registered
    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
      reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;
    }

    // Check if already subscribed
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    // Register with backend
    await axios.post(`${API}/web-push/subscribe`, {
      user_id: userId,
      subscription: sub.toJSON(),
    });

    localStorage.setItem(WEB_PUSH_REGISTERED_KEY, new Date().toISOString());
    return true;
  } catch (e) {
    console.warn('[HYPSEYA] Web push subscription failed:', e.message);
    return false;
  }
}

// ── Message templates (used for local fallback) ───────────────────────────────
function pickMessage({ eriDelta, dayOfWeek, missedDays, eriScore, firstName }) {
  const name = firstName ? `, ${firstName}` : '';

  if (eriDelta != null && eriDelta <= -8)
    return { title: 'HYPSEYA', body: `Your ERI dropped ${Math.abs(eriDelta)} points. Your nervous system is asking for something. 2 minutes?` };

  if (dayOfWeek) {
    const hard = ['Friday', 'Sunday', 'Monday'];
    if (hard.includes(dayOfWeek))
      return { title: 'HYPSEYA', body: `${dayOfWeek}s have been your most difficult days${name}. A short practice now?` };
  }

  if (missedDays >= 3)
    return { title: `${missedDays} days since your last practice`, body: `Regulation is a skill, not a state${name}. Even 2 minutes today keeps the thread alive.` };

  if (missedDays === 2)
    return { title: 'HYPSEYA', body: `Yesterday passed without a session. Your body noticed${name}. Today is still here.` };

  if (eriDelta != null && eriDelta >= 10)
    return { title: 'Something is shifting', body: `Your ERI is up ${eriDelta} points${name}. This is what practice looks like in data.` };

  if (eriScore != null && eriScore >= 75)
    return { title: 'HYPSEYA', body: `ERI ${Math.round(eriScore)} — you're in a strong window${name}. A good time to go deeper.` };

  const defaults = [
    `Your practice is waiting${name}. The nervous system responds to consistency, not intensity.`,
    `One session today${name}. That's the whole instruction.`,
    `The body keeps the score${name}. So does HYPSEYA — and yours is looking good.`,
  ];
  return { title: 'HYPSEYA', body: defaults[Math.floor(Math.random() * defaults.length)] };
}

// ── Analyse local data for message personalisation ────────────────────────────
function analyseLocalData() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);

    const eriRaw = localStorage.getItem(`hyp_eri_${user.user_id}`);
    let eriScore = null, eriDelta = null;
    if (eriRaw) {
      const hist = JSON.parse(eriRaw);
      if (hist.length >= 2) {
        eriScore = hist[hist.length - 1]?.score;
        eriDelta = Math.round(eriScore - hist[0]?.score);
      } else if (hist.length === 1) {
        eriScore = hist[0]?.score;
      }
    }

    const lastSession = localStorage.getItem(`hyp_last_session_${user.user_id}`);
    const missedDays  = lastSession ? Math.floor((Date.now() - new Date(lastSession)) / 86400000) : 0;

    const statsRaw = localStorage.getItem(`hyp_day_stats_${user.user_id}`);
    let hardDay = null;
    if (statsRaw) {
      const stats = JSON.parse(statsRaw);
      let minAvg = Infinity, minDay = null;
      Object.entries(stats).forEach(([day, scores]) => {
        if (!scores?.length) return;
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        if (avg < minAvg) { minAvg = avg; minDay = day; }
      });
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      if (minDay === today) hardDay = today;
    }

    return {
      userId: user.user_id,
      eriScore, eriDelta, missedDays,
      dayOfWeek: hardDay,
      firstName: user.name?.split(' ')[0] || null,
    };
  } catch { return null; }
}

// ── Local notification fallback (in-browser, when app is open) ────────────────
function scheduleLocalNotification(delayMs, title, body) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  setTimeout(() => {
    try {
      const n = new Notification(title, {
        body, icon: '/hypseya-icon.png', badge: '/hypseya-icon.png',
        tag: 'hypseya-smart', requireInteraction: false,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {}
  }, delayMs);
}

// ── Main entry point — called from App.js on mount ────────────────────────────
export async function initSmartNotifications() {
  if (!('Notification' in window)) return;

  // Request permission once per day
  const lastAsked = localStorage.getItem(PERMISSION_ASKED_KEY);
  const today     = new Date().toDateString();

  if (Notification.permission === 'default' && lastAsked !== today) {
    localStorage.setItem(PERMISSION_ASKED_KEY, today);
    await Notification.requestPermission();
  }

  if (Notification.permission !== 'granted') return;

  const data = analyseLocalData();
  if (!data) return;

  // ── 1. Web Push subscription (works when app is closed) ──
  const lastRegistered = localStorage.getItem(WEB_PUSH_REGISTERED_KEY);
  const daysSinceReg   = lastRegistered
    ? (Date.now() - new Date(lastRegistered)) / 86400000 : Infinity;

  // Re-register every 7 days to keep subscription fresh
  if (daysSinceReg > 7 && data.userId) {
    await subscribeToPush(data.userId);
  }

  // ── 2. Local notification for tonight (fallback / complement) ──
  const lastScheduled = localStorage.getItem(STORAGE_KEY);
  if (lastScheduled === today) return;
  localStorage.setItem(STORAGE_KEY, today);

  const { title, body } = pickMessage(data);

  // Schedule for 19:30 local time (or 30min from now if it's past 19:30)
  const now    = new Date();
  const target = new Date();
  target.setHours(19, 30, 0, 0);
  const delay = target > now ? target - now : 30 * 60 * 1000;

  scheduleLocalNotification(delay, title, body);
}

export default initSmartNotifications;
